import { Suspense, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { marked } from 'marked';
import { markedHighlight } from 'marked-highlight';
// `import hljs from 'highlight.js'` pulls in the full bundle - all ~190
// language grammars, ~2.6MB before minification - even though blog posts
// here only ever use a handful. The lean `core` build plus an explicit
// register list covers everything used so far (see content/blog/*.md code
// fences) with real headroom for new posts, at a fraction of the size.
import hljs from 'highlight.js/lib/core';
import bash from 'highlight.js/lib/languages/bash';
import c from 'highlight.js/lib/languages/c';
import cpp from 'highlight.js/lib/languages/cpp';
import css from 'highlight.js/lib/languages/css';
import diff from 'highlight.js/lib/languages/diff';
import go from 'highlight.js/lib/languages/go';
import java from 'highlight.js/lib/languages/java';
import javascript from 'highlight.js/lib/languages/javascript';
import json from 'highlight.js/lib/languages/json';
import markdown from 'highlight.js/lib/languages/markdown';
import plaintext from 'highlight.js/lib/languages/plaintext';
import python from 'highlight.js/lib/languages/python';
import rust from 'highlight.js/lib/languages/rust';
import sql from 'highlight.js/lib/languages/sql';
import typescript from 'highlight.js/lib/languages/typescript';
import xml from 'highlight.js/lib/languages/xml';
import yaml from 'highlight.js/lib/languages/yaml';
import { WIDGETS } from './widgets';

hljs.registerLanguage('bash', bash);
hljs.registerLanguage('shell', bash);
hljs.registerLanguage('c', c);
hljs.registerLanguage('cpp', cpp);
hljs.registerLanguage('css', css);
hljs.registerLanguage('diff', diff);
hljs.registerLanguage('go', go);
hljs.registerLanguage('java', java);
hljs.registerLanguage('javascript', javascript);
hljs.registerLanguage('js', javascript);
hljs.registerLanguage('json', json);
hljs.registerLanguage('markdown', markdown);
hljs.registerLanguage('plaintext', plaintext);
hljs.registerLanguage('python', python);
hljs.registerLanguage('py', python);
hljs.registerLanguage('rust', rust);
hljs.registerLanguage('sql', sql);
hljs.registerLanguage('typescript', typescript);
hljs.registerLanguage('ts', typescript);
hljs.registerLanguage('xml', xml);
hljs.registerLanguage('html', xml);
hljs.registerLanguage('yaml', yaml);
hljs.registerLanguage('yml', yaml);

// Configure marked once (module-level, runs once)
marked.use(
  markedHighlight({
    langPrefix: 'hljs language-',
    highlight(code, lang) {
      const language = hljs.getLanguage(lang) ? lang : 'plaintext';
      return hljs.highlight(code, { language }).value;
    },
  }),
);

marked.use({
  gfm: true,
  breaks: false,
});

// In-body post images are pasted URLs with no known intrinsic size, so
// without explicit width/height attributes Lighthouse flags them as a CLS
// risk (the browser can't reserve their box before the image loads). The
// .md-body img CSS reserves a 16/9 box via aspect-ratio and crops with
// object-fit: cover, which is right for editorial photography but wrong
// for a purpose-made diagram/graphic whose own composition doesn't match
// 16/9 - cover then crops content off the edges, and reserving a generic
// 16/9 box for a much wider image also leaves a tall dead-space gap above
// it while the page loads.
//
// Writing the image's own aspect ratio as the markdown title
// (`![alt](url "fit:1896/830")`) opts it into object-fit: contain via the
// .md-body-img-fit class *and* reserves a box at that exact ratio (browsers
// derive the intrinsic aspect-ratio from width/height attributes directly),
// so the full graphic is always visible, letterboxed, with no oversized
// placeholder and no layout shift once it loads. That's the right fix for a
// WIDE diagram that's merely off from 16/9 - it still reads fine full-width.
//
// A genuinely PORTRAIT image (a photo of people, a tall infographic) is a
// different problem: full-width contain would leave big empty bars on both
// sides, since the box height would have to match a width-scaled box far
// taller than the article column wants to reserve. `portrait:768/1024`
// instead caps the image at 60% of the column width and centers it, like a
// framed figure in editorial writing, and reserves exactly that box (via
// inline style using the real ratio) so there's still no layout shift.
marked.use({
  renderer: {
    image({ href, title, text }) {
      const alt = text ? ` alt="${text}"` : '';
      const fitMatch = /^fit:(\d+)\/(\d+)$/.exec(title ?? '');
      const portraitMatch = /^portrait:(\d+)\/(\d+)$/.exec(title ?? '');
      if (portraitMatch) {
        const [, w, h] = portraitMatch;
        return `<img src="${href}"${alt} class="md-body-img-portrait" width="${w}" height="${h}" loading="lazy" decoding="async">`;
      }
      const cls = fitMatch ? ' class="md-body-img-fit"' : '';
      const [width, height] = fitMatch ? [fitMatch[1], fitMatch[2]] : ['1600', '900'];
      return `<img src="${href}"${alt}${cls} width="${width}" height="${height}" loading="lazy" decoding="async">`;
    },
  },
});

interface Props {
  content: string;
}

// Inline embed syntax: a paragraph containing only {{widget:some-slug}}
// becomes a mount point for a registered interactive React widget.
const WIDGET_TOKEN = /^\{\{widget:([\w-]+)\}\}$/;

export default function MarkdownRenderer({ content }: Props) {
  const ref = useRef<HTMLDivElement>(null);
  const [widgetMounts, setWidgetMounts] = useState<{ el: HTMLElement; slug: string }[]>([]);

  const html = useMemo(() => marked.parse(content) as string, [content]);

  // Post-render DOM decoration: external links open in new tabs, and code
  // blocks get a header card (language label + copy button). Runs after
  // every html change; wrappers live inside the innerHTML so they are wiped
  // and rebuilt on re-render - no double wrapping.
  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    el.querySelectorAll<HTMLAnchorElement>('a[href^="http"]').forEach(a => {
      a.target = '_blank';
      a.rel = 'noopener noreferrer';
    });

    const COPY_ICON =
      '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect width="14" height="14" x="8" y="8" rx="2" ry="2"/><path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2"/></svg>';
    const CHECK_ICON =
      '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6 9 17l-5-5"/></svg>';

    el.querySelectorAll('pre').forEach(pre => {
      // Guard against StrictMode's double effect invocation in dev: once a
      // <pre> is wrapped in a .code-card, skip it on the re-run instead of
      // wrapping it again and leaving an empty shell card behind.
      if (pre.parentElement?.classList.contains('code-card')) return;
      const code = pre.querySelector('code');
      if (!code) return;
      const lang = (code.className.match(/language-([\w+-]+)/)?.[1] ?? 'code')
        .replace('plaintext', 'text');

      const card = document.createElement('div');
      card.className = 'code-card';
      pre.replaceWith(card);

      const header = document.createElement('div');
      header.className = 'code-card-header';

      const langLabel = document.createElement('span');
      langLabel.className = 'code-card-lang';
      langLabel.textContent = lang;

      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'code-copy-btn';
      btn.innerHTML = `${COPY_ICON}<span>copy</span>`;
      btn.addEventListener('click', async () => {
        const text = code.innerText;
        try {
          await navigator.clipboard.writeText(text);
        } catch {
          // Clipboard API can be unavailable (http, old browsers)
          const ta = document.createElement('textarea');
          ta.value = text;
          document.body.appendChild(ta);
          ta.select();
          document.execCommand('copy');
          ta.remove();
        }
        btn.classList.add('copied');
        btn.innerHTML = `${CHECK_ICON}<span>copied</span>`;
        setTimeout(() => {
          btn.classList.remove('copied');
          btn.innerHTML = `${COPY_ICON}<span>copy</span>`;
        }, 1600);
      });

      header.append(langLabel, btn);
      card.append(header, pre);
    });

    // Wide tables (common in AI-generated answers) overflow narrow viewports.
    // Wrap each in a horizontally scrollable container instead of letting it
    // clip or push the page wider. Same double-invoke guard as the <pre> pass.
    el.querySelectorAll('table').forEach(table => {
      if (table.parentElement?.classList.contains('table-scroll')) return;
      const wrap = document.createElement('div');
      wrap.className = 'table-scroll';
      table.replaceWith(wrap);
      wrap.append(table);
    });

    // Widget embeds: a lone paragraph like {{widget:containment-check}}
    // becomes a mount point that a portal renders the registered React
    // component into (see ./widgets/index.ts). Existing mounts are collected
    // too (not just newly-created ones) so StrictMode's double effect
    // invocation in dev doesn't wipe out the first pass's mounts.
    const mounts: { el: HTMLElement; slug: string }[] = [];
    el.querySelectorAll('.widget-mount').forEach(mount => {
      const slug = (mount as HTMLElement).dataset.widgetSlug;
      if (slug) mounts.push({ el: mount as HTMLElement, slug });
    });
    el.querySelectorAll('p').forEach(p => {
      const match = p.textContent?.trim().match(WIDGET_TOKEN);
      if (!match) return;
      const slug = match[1];
      if (!WIDGETS[slug]) return;
      const mount = document.createElement('div');
      mount.className = 'widget-mount';
      mount.dataset.widgetSlug = slug;
      p.replaceWith(mount);
      mounts.push({ el: mount, slug });
    });
    setWidgetMounts(mounts);
  }, [html]);

  return (
    <>
      <div
        ref={ref}
        className="md-body"
        dangerouslySetInnerHTML={{ __html: html }}
      />
      {widgetMounts.map(({ el, slug }) => {
        const Widget = WIDGETS[slug];
        return createPortal(
          <Suspense fallback={null}>
            <Widget />
          </Suspense>,
          el,
          slug,
        );
      })}
      <style>{`
        /* ── Markdown body ─────────────────────────────────────── */
        .md-body {
          font-family: 'DM Sans', sans-serif;
          font-size: 1.05rem;
          line-height: 1.82;
          color: var(--text-muted);
        }

        .md-body h1,
        .md-body h2,
        .md-body h3,
        .md-body h4 {
          font-family: 'Syne', sans-serif;
          font-weight: 700;
          color: var(--text);
          letter-spacing: -0.02em;
          margin: 2.2em 0 0.6em;
          line-height: 1.25;
        }
        .md-body h1 { font-size: 1.85rem; }
        .md-body h2 { font-size: 1.35rem; border-bottom: 1px solid var(--border); padding-bottom: 0.35em; }
        .md-body h3 { font-size: 1.1rem; }
        .md-body h4 { font-size: 0.95rem; text-transform: uppercase; letter-spacing: 0.06em; color: var(--text-dim); }

        .md-body p {
          margin: 0 0 1.35em;
        }

        .md-body a {
          color: var(--accent);
          text-decoration: underline;
          text-underline-offset: 3px;
          transition: color 0.15s;
        }
        .md-body a:hover { color: var(--accent-hover); }

        .md-body strong {
          font-weight: 600;
          color: var(--text);
        }
        .md-body em {
          font-style: italic;
          color: var(--text);
        }

        .md-body ul,
        .md-body ol {
          margin: 0 0 1.35em 1.2em;
          padding: 0;
        }
        .md-body li { margin-bottom: 0.38em; }

        /* Blockquote */
        .md-body blockquote {
          margin: 1.8em 0;
          padding: 1em 1.4em;
          border-left: 3px solid var(--accent-dim);
          background: var(--surface-2);
          border-radius: 0 8px 8px 0;
          color: var(--text-muted);
          font-style: italic;
        }
        .md-body blockquote p { margin: 0; }

        /* Horizontal rule */
        .md-body hr {
          border: none;
          border-top: 1px solid var(--border);
          margin: 2.5em 0;
        }

        /* Inline code */
        .md-body code:not(pre code) {
          font-family: 'JetBrains Mono', monospace;
          font-size: 0.85em;
          background: var(--surface-2);
          border: 1px solid var(--border);
          padding: 2px 6px;
          border-radius: 4px;
          color: var(--accent);
        }

        /* Code cards (header with language + copy button, body with code) */
        .md-body .code-card {
          margin: 1.6em 0;
          border: 1px solid var(--border);
          border-radius: 10px;
          overflow: hidden;
          background: var(--surface-2);
        }
        .md-body .code-card-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 6px 8px 6px 14px;
          background: var(--surface);
          border-bottom: 1px solid var(--border);
        }
        .md-body .code-card-lang {
          font-family: 'JetBrains Mono', monospace;
          font-size: 0.68rem;
          color: var(--text-dim);
          letter-spacing: 0.06em;
          text-transform: lowercase;
        }
        .md-body .code-copy-btn {
          display: flex;
          align-items: center;
          gap: 5px;
          background: transparent;
          border: 1px solid transparent;
          border-radius: 6px;
          padding: 4px 9px;
          font-family: 'JetBrains Mono', monospace;
          font-size: 0.68rem;
          color: var(--text-dim);
          cursor: pointer;
          transition: all 0.15s ease;
        }
        .md-body .code-copy-btn:hover {
          color: var(--accent);
          border-color: var(--border);
          background: var(--surface-2);
        }
        .md-body .code-copy-btn.copied {
          color: var(--accent);
        }
        .md-body pre {
          margin: 1.6em 0;
          padding: 0;
          border-radius: 10px;
          overflow: hidden;
          border: 1px solid var(--border);
          background: var(--surface-2);
        }
        .md-body .code-card pre {
          margin: 0;
          border: none;
          border-radius: 0;
        }
        .md-body pre code {
          font-family: 'JetBrains Mono', monospace;
          font-size: 0.82rem;
          line-height: 1.7;
          display: block;
          padding: 1.2em 1.4em;
          overflow-x: auto;
          background: transparent;
          color: var(--text);
        }

        /* highlight.js overrides - works in both dark & light */
        .md-body .hljs { background: transparent; }
        .md-body .hljs-keyword,
        .md-body .hljs-selector-tag { color: #8be9fd; }
        .md-body .hljs-string { color: #a8ff78; }
        .md-body .hljs-comment { color: var(--text-dim); font-style: italic; }
        .md-body .hljs-number { color: #ffb86c; }
        .md-body .hljs-function,
        .md-body .hljs-title { color: #50fa7b; }
        .md-body .hljs-type { color: #bd93f9; }
        .md-body .hljs-attr { color: #8be9fd; }
        .md-body .hljs-built_in { color: #ffb86c; }
        [data-theme="light"] .md-body .hljs-keyword,
        [data-theme="light"] .md-body .hljs-selector-tag { color: #007acc; }
        [data-theme="light"] .md-body .hljs-string { color: #008000; }
        [data-theme="light"] .md-body .hljs-comment { color: #999; }
        [data-theme="light"] .md-body .hljs-number { color: #c04; }
        [data-theme="light"] .md-body .hljs-function,
        [data-theme="light"] .md-body .hljs-title { color: #005a9e; }
        [data-theme="light"] .md-body .hljs-type { color: #7a3e9d; }
        [data-theme="light"] .md-body .hljs-attr { color: #007acc; }
        [data-theme="light"] .md-body .hljs-built_in { color: #b35900; }

        /* Tables - wrapped in a scroll container so wide tables scroll
           horizontally on narrow viewports instead of clipping or forcing
           the whole page wider. */
        .md-body .table-scroll {
          overflow-x: auto;
          -webkit-overflow-scrolling: touch;
          margin: 1.6em 0;
          border-radius: 8px;
        }
        .md-body table {
          width: 100%;
          min-width: 480px;
          border-collapse: collapse;
          margin: 0;
          font-size: 0.9rem;
        }
        .md-body th {
          background: var(--surface-2);
          border: 1px solid var(--border);
          padding: 10px 14px;
          text-align: left;
          font-family: 'JetBrains Mono', monospace;
          font-size: 0.75rem;
          color: var(--accent);
          text-transform: uppercase;
          letter-spacing: 0.05em;
          white-space: nowrap;
        }
        .md-body td {
          border: 1px solid var(--border);
          padding: 9px 14px;
          vertical-align: top;
        }
        .md-body tr:nth-child(even) td { background: var(--surface-2); }

        /* Images. In-body markdown images have no known intrinsic size (the
           author just pastes a URL), so without a reserved aspect-ratio the
           page collapses to zero height for that image and then jumps once
           it loads, a real CLS hit on slow connections. 16/9 is a reasonable
           default for editorial photography.
           height: auto below is load-bearing here, not decorative: every
           <img> the renderer emits carries a literal width="1600"
           height="900" HTML attribute pair (for the CLS reservation
           itself). Without an explicit height: auto in CSS, the browser
           honors that height attribute as a literal 900px box once
           max-width: 100% shrinks the width below 1600px - aspect-ratio
           then has nothing left to resize, since height is already
           pinned, and any image narrower than 1600px renders
           squashed/tall instead of at 16/9. */
        .md-body img {
          max-width: 100%;
          height: auto;
          aspect-ratio: 16/9;
          object-fit: cover;
          border-radius: 10px;
          display: block;
          margin: 1.8em auto;
          border: 1px solid var(--border);
          background: var(--surface-2);
        }
        /* Opt-out for purpose-made diagrams/graphics (title="fit:W/H" in the
           markdown source): show the whole image letterboxed instead of
           cropping it to 16/9. aspect-ratio: auto overrides the default
           .md-body img rule's 16/9 (a more-specific selector doesn't drop
           a value from a less-specific one unless it's reset explicitly) -
           with auto, the browser derives the box straight from this img's
           width/height HTML attributes (set by MarkdownRenderer's image
           renderer to the image's real ratio), so the reserved space
           matches the actual graphic instead of an oversized placeholder,
           with no layout shift once it loads. */
        .md-body img.md-body-img-fit {
          width: 100%;
          height: auto;
          aspect-ratio: auto;
          object-fit: contain;
          background: var(--surface-2);
        }
        /* Opt-in for genuinely portrait images (title="portrait:W/H" in the
           markdown source): a photo of people or a tall infographic looks
           wrong either cropped to 16/9 (cuts off heads/content) or shown
           full-width contain (huge empty bars on both sides, since the
           reserved box height would have to match a width-scaled box far
           taller than the column wants). Capping the width instead - like a
           framed figure in editorial writing - keeps the image at a natural
           size with no empty space and no crop. aspect-ratio: auto same as
           .md-body-img-fit, for the same reason: derive the box from this
           img's real width/height attributes, not the 16/9 default. */
        .md-body img.md-body-img-portrait {
          width: min(60%, 420px);
          height: auto;
          aspect-ratio: auto;
          object-fit: contain;
          background: var(--surface-2);
        }

        /* Resource link cards - used for curated "further reading" lists */
        .md-body .resource-list {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
          gap: 12px;
          margin: 1.6em 0;
        }
        .md-body .resource-card {
          position: relative;
          display: flex;
          flex-direction: column;
          gap: 5px;
          padding: 14px 32px 14px 16px;
          border: 1px solid var(--border);
          border-radius: 10px;
          background: var(--surface-2);
          text-decoration: none;
          transition: border-color 0.15s ease, transform 0.15s ease, background 0.15s ease;
        }
        .md-body .resource-card:hover {
          border-color: var(--accent);
          background: var(--accent-glow);
          transform: translateY(-2px);
        }
        .md-body .resource-card::after {
          content: '\\2197';
          position: absolute;
          top: 12px;
          right: 12px;
          font-size: 0.9rem;
          color: var(--text-dim);
          transition: color 0.15s ease, transform 0.15s ease;
        }
        .md-body .resource-card:hover::after {
          color: var(--accent);
          transform: translate(2px, -2px);
        }
        .md-body .resource-kind {
          font-family: 'JetBrains Mono', monospace;
          font-size: 0.62rem;
          text-transform: uppercase;
          letter-spacing: 0.08em;
          color: var(--accent);
        }
        .md-body .resource-title {
          font-family: 'Syne', sans-serif;
          font-weight: 700;
          font-size: 0.92rem;
          color: var(--text);
          line-height: 1.3;
        }
        .md-body .resource-desc {
          font-family: 'DM Sans', sans-serif;
          font-size: 0.8rem;
          line-height: 1.55;
          color: var(--text-muted);
        }

        /* YouTube / video embeds */
        .md-body iframe {
          width: 100%;
          aspect-ratio: 16/9;
          border-radius: 10px;
          border: 1px solid var(--border);
          margin: 1.6em 0;
          display: block;
        }
        .md-body video {
          width: 100%;
          border-radius: 10px;
          margin: 1.6em 0;
          display: block;
        }
      `}</style>
    </>
  );
}
